import { z } from "zod";
import { referralStatusSchema } from "./referral.js";

/**
 * Admin Referrals oversight — GET /v1/admin/referrals (DF-9 audit/DEADFLOW.md, 2026-07-19 ·
 * @UC9177 referrals resource).
 *
 * DEAD-FLOW NÓ VÁ: resource admin `/referrals` khai `live:true` (resources.ts) nhưng BE KHÔNG có
 * endpoint list per-referral — màn empty-state vĩnh viễn (số liệu aggregate nằm riêng ở
 * /admin/analytics/referrals @UC9093). Endpoint này list TỪNG ca giới thiệu READ-ONLY, cursor keyset
 * (createdAt DESC, id DESC) — mirror oversight pattern (abuse-reports 0043).
 *
 * PII (C2): referrerName/referredName do server resolve + REDACT theo vai (NameResolverService —
 * vai đọc-thấp thấy email/phone MASK). userId là con trỏ, KHÔNG PII. `code` là mã referral CSPRNG.
 */

/** Giới hạn limit list (mirror TRUST_LIST_MAX_LIMIT oversight). */
export const ADMIN_REFERRAL_LIST_MAX_LIMIT = 100;

/** Một ca giới thiệu (row admin oversight). */
export const adminReferralItemSchema = z
  .object({
    id: z.string(),
    /** Mã referral (CSPRNG, KHÔNG PII). */
    code: z.string(),
    referrerUserId: z.string(),
    /** Tên người giới thiệu — server resolve + redact theo vai; null nếu không resolve được. */
    referrerName: z.string().nullable(),
    /** Người được mời — null khi referral chưa convert (chưa gắn user). */
    referredUserId: z.string().nullable(),
    referredName: z.string().nullable(),
    /** Kênh attribution (zalo/link/qr…) — null nếu không ghi nhận. */
    channel: z.string().nullable(),
    status: referralStatusSchema,
    createdAt: z.string(),
    convertedAt: z.string().nullable(),
  })
  .strict();
export type AdminReferralItem = z.infer<typeof adminReferralItemSchema>;

/** Query list — filter status + cursor keyset + limit (mirror abuseReportListQuerySchema). */
export const adminReferralListQuerySchema = z
  .object({
    status: referralStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_REFERRAL_LIST_MAX_LIMIT).optional(),
  })
  .strict();
export type AdminReferralListQuery = z.infer<typeof adminReferralListQuerySchema>;

export const adminReferralListResponseSchema = z
  .object({
    data: z.array(adminReferralItemSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();
export type AdminReferralListResponse = z.infer<typeof adminReferralListResponseSchema>;
